import { autorun } from "mobx";
import { TaxStatus } from "../enums";
import {
  IAPICreateSellerModel,
  IAPILinkedSellerModel,
  IAPISellerCommunicationSettingsModel,
  IAPISellerLinkedMerchantAccountModel,
} from "../interfaces";
import {
  CoreInputModelUtil,
  CoreModel,
  getNewErrors,
  InputErrorModel,
} from "@mindmemobile/mm-shared-global";
import { CountryWithStateModel } from "./CountryModels";
import { MediaModel } from "./MediaModel";
import { AuthService } from "../services";

export class CreateSellerModel extends CoreModel {
  id: string;
  name: string;
  isActive: boolean;
  email: string;
  phoneCountry: CountryWithStateModel;
  phoneNumber: string;
  countryDetails: CountryWithStateModel;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  taxStatus: keyof typeof TaxStatus;
  taxCountryDetails: CountryWithStateModel;
  taxId: string;
  logo?: MediaModel;
  communicationSettings: SellerCommunicationSettingsModel;
  merchantAccountId: string;
  linkedMerchants: SellerLinkedMerchantAccountModel[];
  errors: InputErrorModel<CreateSellerModel>[];

  constructor(data?: Partial<CreateSellerModel>) {
    super();
    this.id = (data && data.id) || "";
    this.name = (data && data.name) || "";
    this.isActive = (data && data.isActive) || false;
    this.email = data?.email || "";
    this.phoneCountry =
      (data && data.phoneCountry) || new CountryWithStateModel();
    this.phoneNumber = data?.phoneNumber || "";
    this.countryDetails = data?.countryDetails || new CountryWithStateModel();
    this.address1 = data?.address1 || "";
    this.address2 = data?.address2 || "";
    this.city = data?.city || "";
    this.state = data?.state || "";
    this.zip = data?.zip || "";
    this.taxStatus = data?.taxStatus || TaxStatus.Taxable;
    this.taxCountryDetails =
      data?.taxCountryDetails || new CountryWithStateModel();
    this.taxId = data?.taxId || "";
    this.logo = data?.logo && new MediaModel(data?.logo);
    this.communicationSettings = new SellerCommunicationSettingsModel(
      data?.communicationSettings
    );
    this.merchantAccountId = data?.merchantAccountId || "";
    this.linkedMerchants =
      data?.linkedMerchants?.map(
        (merchant) => new SellerLinkedMerchantAccountModel(merchant)
      ) || [];
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CreateSellerModel>(e);
        })) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  static deserializeAPIModel(
    data: IAPICreateSellerModel,
    countries: CountryWithStateModel[]
  ): CreateSellerModel {
    const selectedCountry = countries.find(
      (country) => country.id === data.address?.countryId
    );
    const defaultCountry = countries.find(
      (country) => country.abbreviationName.toLowerCase() === "us"
    );
    const selectedPhoneCountry =
      data.phone.countryCode &&
      countries.find((country) => country.id === data.phone.countryId);
    const selectedTaxCountry =
      data.taxCountryId &&
      countries.find((country) => country.id === data.taxCountryId);
    const mappedData: Partial<CreateSellerModel> = {
      id: data.id,
      name: data.name,
      isActive: data.isActive,
      phoneCountry: selectedPhoneCountry
        ? new CountryWithStateModel(selectedPhoneCountry)
        : new CountryWithStateModel(defaultCountry),
      phoneNumber:
        data.phone && data.phone.phoneNumber ? data.phone.phoneNumber : "",
      countryDetails: selectedCountry 
        ? new CountryWithStateModel(selectedCountry)
        : new CountryWithStateModel(defaultCountry),
      address1: data.address ? data.address.address1 : "",
      address2: data.address ? data.address.address2 : "",
      city: data.address ? data.address.city : "",
      state: data.address?.stateId || "",
      zip: data.address ? data.address.zipCode : "",
      email: data.email || "",
      taxId: data.taxId ? data.taxId : "",
      taxCountryDetails: selectedTaxCountry
        ? new CountryWithStateModel(selectedTaxCountry)
        : new CountryWithStateModel(defaultCountry),
      taxStatus: data.taxStatus
        ? (data.taxStatus as keyof typeof TaxStatus)
        : TaxStatus.Taxable,
      logo: data?.logo ? MediaModel.deserializeAPIModel(data?.logo) : undefined,
      communicationSettings: new SellerCommunicationSettingsModel(
        data?.communicationSettings
      ),
      linkedMerchants:
        data?.linkedMerchants?.map(
          (merchant) => new SellerLinkedMerchantAccountModel(merchant)
        ) || [],
    };
    return new CreateSellerModel(mappedData);
  }

  hasError(field: keyof CreateSellerModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getError(field: keyof CreateSellerModel): string {
    const error = this.errors.find((e) => e.field === field);
    return error?.message || "";
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CreateSellerModel>[] = [];
    CoreInputModelUtil.validateTextField(this.name, "name", newErrors);
    CoreInputModelUtil.validateEmail("email", this.email, newErrors);
    CoreInputModelUtil.validateTextField(this.address1, "address1", newErrors);
    CoreInputModelUtil.validateTextField(this.address2, "address2", newErrors);
    CoreInputModelUtil.validateTextField(this.city, "city", newErrors);
    CoreInputModelUtil.validateTextField(this.state, "state", newErrors);
    CoreInputModelUtil.validateTextField(this.zip, "zip", newErrors);
    CoreInputModelUtil.validateTextField(
      this.phoneCountry.name,
      "phoneCountry",
      newErrors
    );
    CoreInputModelUtil.validatePhoneNumber(
      "phoneNumber",
      this.phoneNumber,
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.countryDetails.name,
      "countryDetails",
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.taxStatus,
      "taxStatus",
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.taxCountryDetails.name,
      "taxCountryDetails",
      newErrors
    );
    if (TaxStatus[this.taxStatus] === TaxStatus.TaxExempt) {
      CoreInputModelUtil.validateTaxId("taxId", this.taxId, newErrors);
    }

    const isValidCommunicationSettings =
      this.communicationSettings.validate(isAutoRun);
    if (!isValidCommunicationSettings) {
      this.addError("communicationSettings", newErrors);
    }
    // const isValidLogo = this.logo.validate();
    // if (!isValidLogo) {
    //   this.addError("logo", newErrors, "Logo is required field");
    // }
    const invalidLinkedMerchants = this.linkedMerchants.some(
      (merchant) => !merchant.validate(isAutoRun)
    );
    if (invalidLinkedMerchants) {
      this.addError("linkedMerchants", newErrors);
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  createAPIModel(): IAPICreateSellerModel {
    return {
      id: this.id,
      isActive: this.isActive,
      email: this.email,
      phone: {
        countryCode: this.phoneCountry.code.toString(),
        countryId: this.phoneCountry.id,
        phoneNumber: this.phoneNumber,
      },
      address: {
        country: this.countryDetails.name,
        countryId: this.countryDetails.id,
        city: this.city,
        zipCode: this.zip,
        countryCode: this.countryDetails.code.toString(),
        stateId: this.state,
        state:
          this.countryDetails.states.find((state) => state.id === this.state)
            ?.name || "",
        address1: this.address1,
        address2: this.address2,
        address3: "",
      },
      accountId: AuthService.authData.accountId,
      name: this.name,
      taxStatus: this.taxStatus,
      taxCountryId: this.taxCountryDetails.id,
      taxId: this.taxId,
      logo: this.logo?.createAPIModel(),
      communicationSettings: this.communicationSettings?.createAPIModel(),
      linkedMerchants:
        this.linkedMerchants?.map((merchant) => merchant.createAPIModel()) ||
        [],
    };
  }
}

export class SellerCommunicationSettingsModel {
  fromEmail: string;
  fromText: string;
  errors: InputErrorModel<SellerCommunicationSettingsModel>[];
  constructor(data?: Partial<SellerCommunicationSettingsModel>) {
    this.fromEmail = data?.fromEmail || "";
    this.fromText = data?.fromText || "";
    this.errors = data?.errors || [];
  }
  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<SellerCommunicationSettingsModel>[] = [];
    CoreInputModelUtil.validateEmail("fromEmail", this.fromEmail, newErrors);
    CoreInputModelUtil.validateTextField(this.fromText, "fromText", newErrors);
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }
  hasError(field: keyof SellerCommunicationSettingsModel): boolean {
    return this.errors.some((e) => e.field === field);
  }
  deserializeAPIModel(
    data?: IAPISellerCommunicationSettingsModel
  ): SellerCommunicationSettingsModel {
    const mappedData: Partial<SellerCommunicationSettingsModel> = {
      fromEmail: data?.fromEmail || "",
      fromText: data?.fromText || "",
    };
    return new SellerCommunicationSettingsModel(mappedData);
  }
  createAPIModel(): IAPISellerCommunicationSettingsModel {
    return { fromEmail: this.fromEmail, fromText: this.fromText };
  }
}

export class SellerLinkedMerchantAccountModel {
  gateway: string;
  merchantAccountId: string;
  applicationId: string;
  name: string;
  isPrimary: boolean;
  errors: InputErrorModel<SellerLinkedMerchantAccountModel>[];
  constructor(data?: Partial<SellerLinkedMerchantAccountModel>) {
    this.gateway = data?.gateway || "";
    this.merchantAccountId = data?.merchantAccountId || "";
    this.applicationId = data?.applicationId || "";
    this.name = data?.name || "";
    this.isPrimary = data?.isPrimary || false;
    this.errors = data?.errors || [];
  }
  deserializeAPIModel(
    data?: IAPISellerLinkedMerchantAccountModel
  ): SellerLinkedMerchantAccountModel {
    const mappedData: Partial<SellerLinkedMerchantAccountModel> = {
      gateway: data?.gateway || "",
      merchantAccountId: data?.merchantAccountId || "",
      applicationId: data?.applicationId || "",
      name: data?.name || "",
      isPrimary: data?.isPrimary || false,
    };
    return new SellerLinkedMerchantAccountModel(mappedData);
  }
  createAPIModel(): IAPISellerLinkedMerchantAccountModel {
    return {
      gateway: this.gateway,
      merchantAccountId: this.merchantAccountId,
      applicationId: this.applicationId,
      name: this.name,
      isPrimary: this.isPrimary,
    };
  }
  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<SellerLinkedMerchantAccountModel>[] = [];
    CoreInputModelUtil.validateTextField(
      this.merchantAccountId,
      "merchantAccountId",
      newErrors
    );
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }
}

export class SellerModel {
  id: string;
  name: string;
  isActive: boolean;
  logoURL: string;

  constructor(data?: Partial<SellerModel>) {
    this.id = (data && data.id) || "";
    this.name = (data && data.name) || "";
    this.isActive = (data && data.isActive) || false;
    this.logoURL = data?.logoURL || "";
  }

  static deserializeAPIModel(data: IAPILinkedSellerModel): SellerModel {
    if (!data) {
      return new SellerModel();
    }

    const mappedData: Partial<SellerModel> = {
      id: data.id,
      name: data.name,
      isActive: data.isActive,
      logoURL: data.logoURL || "",
    };
    return new SellerModel(mappedData);
  }
  createAPIModel(): IAPILinkedSellerModel {
    return {
      id: this.id,
      isActive: this.isActive,
      name: this.name,
      logoURL: this.logoURL,
    };
  }
}
